import { type ExplorationSpec } from '@/designs-dev/explore/types';

import {
  A1SetPassword,
  A2WhoFor,
  A3ConditionalForm,
  A4Conversation,
  A5CareCircle,
  A6Eligibility,
  A7ParkedAction,
} from '@/karehero-app/explore/tell-us-once/WayIn';
import {
  F2Home,
  F3WhoAreYou,
  F5Typing,
  F7AaStart,
  F8Skip,
  F10Review,
  F11Submitted,
} from '@/karehero-app/explore/tell-us-once/HighLevelFlow';
import {
  B1ClaimsHub,
  B2SingleClaim,
} from '@/karehero-app/explore/tell-us-once/ClaimsApp';
import B3DataMap from '@/karehero-app/explore/tell-us-once/DataMap';
import {
  C1Translation,
  C2PhotoOrPrecise,
  C3ConfirmEdit,
  C4Review,
  C5NotIntoTheVoid,
  C6WhenItStops,
} from '@/karehero-app/explore/tell-us-once/AaFlow';
import {
  D1DiaryReach,
  D2DiaryReview,
  D3Calendar,
} from '@/karehero-app/explore/tell-us-once/Diary';
import {
  E1PickUp,
  E2HomeResume,
} from '@/karehero-app/explore/tell-us-once/DropInOut';
import AaFormCarousel from '@/karehero-app/explore/tell-us-once/AaForm';
import AaPrompt from '@/karehero-app/explore/tell-us-once/AaPrompt';

// Tell Us Once — divergence for the Monday 10 August reconvene.
//
// Nothing here is decided. Every frame is something to see and react to, not a
// proposal. Where a direction is leaned into, it is only so that one can be
// explored further — the doc this is built from says so, and the page repeats
// it before it shows anything.
//
// Section F is the whole journey in order, at the lowest fidelity that still
// tells the story. It reuses the lettered sections' screens wherever they
// already exist, so the flow can't drift from the frames that argue about it —
// those reused steps are marked flowOnly and carry a pointer instead of a
// second brief.
//
// Section A then runs the way in as one ordered set: the existing set-password
// page for reference (A1), the first thing we ask (A2), the fork asked two
// opposite ways (A3 and A4), what is woven in (A5), what it pays off into
// (A6), and where a deferred answer lands (A7). Only A3 and A4 carry a trade —
// they are the genuine rivals; the rest are steps around them.

export const tellUsOnceSpec: ExplorationSpec = {
  id: 'tell-us-once',
  sourceFile:
    'front-end/designs/src/karehero-app/explore/tell-us-once/tell-us-once.tsx',
  title: 'Tell us once',
  by: 'Vez',
  occasion:
    'Rough low-fi frames to react to at the Monday 10 August reconvene. Sketched to make the abstract concrete enough to choose together, with options in front of us instead of words in a doc.',
  stance:
    'This is divergence. Nothing here is decided. Every frame is something to see, not something being proposed. Where a direction has been leaned into, it is only so it can be explored a bit deeper — not because it is settled.',

  readingNotes: [
    'Onboarding and calculator — two rival ways in, both rough. Exploring which feels right, not building one.',
    'AA end to end — the deepest, because it is the most likely first candidate to try whole. Conversation-first, because that is the version most worth seeing working.',
    'Ambient diary — a basic sketch. Enough to ask the questions, not to answer them.',
    'Dropping in and out — two ways to come back, both rough. The principle matters more than either sketch.',
    'High level flow — the whole journey in one strip. Start here, then use the lettered sections to argue with individual moments.',
    'Roughness is matched to how unsure we are, not to how important something is.',
  ],

  sections: [
    {
      id: 'F',
      title: 'High level flow',
      intent:
        'The whole journey end to end, at the lowest fidelity that still tells the story: account, first task, the conversation, the reveal, the claim, and what happens when you skip something. The lettered sections each pull one moment out of this and argue with it.',
      mode: 'sequence',
      fidelity: 'rough',
      frames: [
        {
          id: 'F1',
          name: 'Set your password',
          flowOnly: true,
          inTheFrame: 'The existing Kinde hosted set-password page.',
          chasing: 'Nothing. It is where everyone arrives from.',
          render: () => <A1SetPassword />,
        },
        {
          id: 'F2',
          name: 'Home, with one task',
          tap: 'Account created',
          inTheFrame:
            'Home with a single task: tell us about your care situation. One rather than a list, because at this point there is exactly one thing worth doing.',
          copy: {
            lines: [
              'Tell us about your care situation. About 5 minutes. We’ll work out what you might be owed.',
            ],
          },
          chasing:
            'Does the app need a home before it knows anything? Or is the first task the whole screen until there is something else to show?',
          render: () => <F2Home />,
        },
        {
          id: 'F3',
          name: 'Who is asking',
          tap: 'Tap the task, or just start talking',
          inTheFrame:
            'The conversation establishing whether they are a carer or claiming for themselves, then handing over to typed fields for the names — and saying why.',
          copy: {
            exchange: [
              {
                from: 'us',
                text: 'First up — are you caring for someone, or looking into this for yourself?',
              },
              { from: 'user', text: 'I look after my mum.' },
              {
                from: 'us',
                text: 'Got it. Names have to be spot on for the claim, so type these rather than saying them.',
              },
            ],
          },
          chasing:
            'The branch is the point: a carer has two people to name, someone claiming for themselves has one. Asking a self-claimant for “their” name as well is exactly the clumsiness this whole idea is meant to remove — so the very first question has to fork cleanly. Names are typed rather than spoken because a misheard name is a rejected claim, which makes this the first place the conversation has to admit a limit.',
          render: () => <F3WhoAreYou />,
        },
        {
          id: 'F4',
          name: 'Care-circle invite, woven in',
          tap: 'A few questions later',
          flowOnly: true,
          inTheFrame:
            'The same care-circle moment as A5, shown here in sequence so it reads as part of the flow rather than a detour.',
          chasing: 'Covered at A5.',
          render: () => <A5CareCircle />,
        },
        {
          id: 'F5',
          name: 'When they have to type',
          tap: 'Something a spoken answer can’t carry',
          inTheFrame:
            'An NI number being typed, caught mid-entry with the keyboard up. Not a spoken answer, not a photo — the case where only exact characters will do.',
          copy: {
            lines: [
              'I’ll need Margaret’s National Insurance number for this. It’s on her payslips or pension letters.',
            ],
          },
          chasing:
            'How abrupt is the handoff from talking to typing when everything either side of it is spoken? And does telling them where to find it do enough work to stop this being the moment they drop out?',
          render: () => <F5Typing />,
        },
        {
          id: 'F6',
          name: 'Here’s what you can claim',
          tap: 'Enough answered to score it',
          flowOnly: true,
          inTheFrame: 'The eligibility reveal, as A6.',
          chasing: 'Covered at A6.',
          render: () => <A6Eligibility />,
        },
        {
          id: 'F7',
          name: 'Starting Attendance Allowance',
          tap: 'Tap ‘Start with Attendance Allowance’',
          inTheFrame:
            'The claim opening as more of the same conversation, leading with what it already knows rather than asking again. Compressed — section C has the whole thing.',
          copy: {
            exchange: [
              {
                from: 'us',
                text: 'Right, Attendance Allowance. I already know she needs help washing, so I won’t ask again.',
              },
              { from: 'us', text: 'How are her nights?' },
            ],
          },
          chasing:
            'Does a claim need to announce itself as a claim at all? If it opens by naming what it already has, the boundary between the calculator and the application may not need to exist.',
          render: () => <F7AaStart />,
        },
        {
          id: 'F8',
          name: 'Skipping something',
          tap: 'A question they can’t answer now',
          inTheFrame:
            'The defer moment: skip for now, or snap a photo. The same pattern as C2, in the flow so the story includes not having the answer to hand.',
          copy: {
            lines: ['Next I need her GP’s address. Do you have it handy?'],
          },
          chasing:
            'How freely can someone skip before the claim stalls? And does offering the photo route here make skipping less likely, or just give them a third thing to decide about?',
          render: () => <F8Skip />,
        },
        {
          id: 'F9',
          name: 'Review, answered inline',
          tap: 'Everything else covered',
          inTheFrame:
            'The pre-submission view with the parked items in their own block at the top, answerable in place — one shown mid-answer. Confirmed sections collapse to a count underneath.',
          copy: {
            lines: [
              'This is everything we’ll send. Two things still need you. Answer it here. No need to go anywhere.',
            ],
          },
          chasing:
            'This is the answer to “where do the later items go” — they come back at review and get finished without leaving. Does that make skipping safe enough to encourage, and is a parked item at review still the same question, or does it need re-asking with the context you had when you skipped it?',
          render: () => <F10Review />,
        },
        {
          id: 'F10',
          name: 'Submitted, and a call offered',
          tap: 'Submit the claim',
          inTheFrame:
            'Confirmation, then a call with the care team offered as the next thing — not required, and not a step to clear.',
          copy: {
            lines: [
              'That’s gone in. We’ll let you know as soon as we hear anything.',
              'Book a call with your care expert. They can check what else Margaret might be owed and help with the next claim.',
            ],
          },
          chasing:
            'Is the call the reward or the fallback? Offered here it reads as an upgrade, but if the claim needed a human to be trustworthy then it belongs earlier and this whole flow is the wrong shape.',
          render: () => <F11Submitted />,
        },
        {
          id: 'F11',
          name: 'The skipped bits, parked',
          aside: true,
          inTheFrame:
            'Not a step in the flow — where a skipped item can be found between skipping it and reaching review. The actions list, as A7.',
          chasing:
            'Does this need to exist at all? Review already catches everything that was skipped, so this only earns its place for someone who wants to clear things before they get there.',
          render: () => <A7ParkedAction />,
        },
      ],
    },

    {
      id: 'A',
      title: 'The way in',
      intent:
        'What if the calculator is the front door, and onboarding is just the first few things it asks, becoming the fork that shapes the rest of the experience.',
      mode: 'rivals',
      fidelity: 'rough',
      frames: [
        {
          id: 'A1',
          name: 'Set your password',
          flowOnly: true,
          inTheFrame:
            'The existing Kinde hosted set-password page, shown for reference. Not part of what is being questioned — it is here so the fork after it is read in context rather than as a fresh start.',
          chasing:
            'Nothing, deliberately. It is the fixed point the rest of the section moves relative to: whatever the way in becomes, the user arrives here first.',
          render: () => <A1SetPassword />,
        },
        {
          id: 'A2',
          name: 'Tell us who you are caring for',
          flowOnly: true,
          inTheFrame:
            'The first thing asked once the account exists. About the person being cared for rather than the carer, because everything downstream hangs off them.',
          copy: {
            lines: [
              'Who are you caring for? We only need a few things to work out what they might be entitled to.',
            ],
          },
          chasing:
            'How little can we ask here before the calculator takes over? This is the seam where onboarding stops and the calculator starts, and the whole idea is that the seam should not be visible.',
          render: () => <A2WhoFor />,
        },
        {
          id: 'A3',
          name: 'Calculator as a conditional form',
          flowOnly: true,
          inTheFrame:
            'A short branching form. A few questions, each answer opening or closing the next, so it feels like it is narrowing rather than dragging on.',
          optimises:
            'Legibility. The branching is visible, so the user can see it is asking because of what they said.',
          costs:
            'Ambition. It may end up being the government form with better manners.',
          copy: {
            caption:
              'Structured, branching. Explicit logic — which might be the point: whatever we learn here could lift into the conversation version later.',
          },
          chasing:
            'The safer, more legible version. Is it meaningfully better than the government’s own form, or just tidier? And does the branching logic turn out to be reusable no matter which capture model we land on?',
          render: () => <A3ConditionalForm />,
        },
        {
          id: 'A4',
          name: 'Calculator as a conversation',
          inTheFrame:
            'The same outcome, reached by talking — the user describes their situation, and it asks the next thing a good CAM would.',
          optimises:
            'Range. Someone can say the thing that actually matters without it having to match a field first.',
          costs:
            'Visible logic. When the branches are hidden, the user has nothing to check the questioning against.',
          copy: {
            exchange: [
              {
                from: 'us',
                text: 'Tell me a bit about the person you’re caring for.',
              },
              {
                from: 'us',
                text: 'And how are they managing day to day — washing, dressing, getting around?',
              },
            ],
          },
          chasing:
            'The more ambitious version. Can “just talk to us” carry the same eligibility logic without becoming a black box? When the user cannot see the branches, how do they trust it is asking the right things? Same trust question as AA, which is partly why it is worth surfacing this early.',
          render: () => <A4Conversation />,
        },
        {
          id: 'A5',
          name: 'Care-circle invite, woven in',
          maybe: true,
          inTheFrame:
            'A light “who else is helping?” moment inside the flow that lets someone invite others into a shared care circle. Asked in the conversation rather than as its own form screen — answered by typing an email, with an explicit way to park it.',
          copy: {
            exchange: [
              {
                from: 'us',
                text: 'Caring is rarely a solo job. Is anyone else helping with Margaret?',
              },
              { from: 'user', text: 'Yes, my sister helps me.' },
              {
                from: 'us',
                text: 'Pop her email in and I’ll invite her. She’ll see the same claims you do.',
              },
            ],
          },
          chasing:
            'Exploring where this would naturally slot into and whether two people feeding one record changes anything downstream.',
          render: () => <A5CareCircle />,
        },
        {
          id: 'A6',
          name: 'Here’s what you can claim',
          inTheFrame:
            'The moment the calculator has run and the app reveals what this person looks eligible for — benefits appearing as outcomes, leading into the first claim.',
          copy: {
            lines: [
              'Based on what you’ve told us, Margaret could be entitled to: Attendance Allowance · Carer’s Allowance · Council Tax reduction.',
            ],
          },
          chasing:
            'If any of this works, this is the moment that earns it — they told us about their person, and we showed them what they are owed. How do we show likely versus confirmed without over-promising, and how does it hand off into starting AA?',
          render: () => <A6Eligibility />,
        },
        {
          id: 'A7',
          name: 'The parked invite, as an action',
          inTheFrame:
            'Where “I’ll do this later” ends up — the real actions screen, seeded with just that one action rather than the stock demo set. Self-created rather than set by a CAM, so it reads as your own to-do.',
          copy: {
            lines: [
              'Add your sister’s email · You said you’d do this later · Regarding Margaret’s care circle',
            ],
          },
          chasing:
            'This is C2’s open question showing up early — where do the “later” items actually live? If a parked item needs a home this soon, the tray is not an AA detail, it is a thing the whole app has. And does one parked item justify a screen, or should it just sit on home?',
          render: () => <A7ParkedAction />,
        },
      ],
    },

    {
      id: 'B',
      title: 'What the app could look like once claims are known',
      intent:
        'Exploring claims as the spine of the app rather than a form buried in a menu. What does the app even become once someone knows what they can claim?',
      mode: 'probes',
      fidelity: 'rough',
      frames: [
        {
          id: 'B1',
          name: 'Home / claims hub',
          inTheFrame:
            'What home might become once eligibility is known — the available claims, each with some state (ready to start, in progress, forming), and a way into AA.',
          copy: {
            caption:
              'Exploring claims as the spine of the app rather than a form buried in a menu.',
          },
          chasing:
            'Is this a to-do list, a progress view, or something warmer? And where does “tell us what’s changed” — the diary — sit from here?',
          render: () => <B1ClaimsHub />,
        },
        {
          id: 'B2',
          name: 'A single claim, and claims still forming',
          inTheFrame:
            'One claim opened up — what is captured, what is left — plus a claim in a “forming” state, part-assembled from what we already know but not properly started.',
          copy: {
            lines: [
              'Attendance Allowance — 60% ready. We’ve filled what we can from what you’ve told us.',
            ],
          },
          chasing:
            'How do we let someone get at a half-formed claim without it feeling like unfinished homework? This is an attempt to picture the claims that are still going to be formed first.',
          render: () => <B2SingleClaim />,
        },
        {
          id: 'B3',
          name: 'Where the data might live',
          bare: true,
          inTheFrame:
            'Not a screen — a rough map. Three layers: global (carer info, care recipient info, GP, NI number — entered once, reused everywhere), claim-specific (the AA care-needs detail), and unstructured (raw brain-dumps, voice, diary notes — kept even when not yet usable).',
          copy: {
            caption:
              'One way to think about it: global data flows into every claim; unstructured capture sits raw and only becomes a claim answer once the user confirms it.',
          },
          chasing:
            'Where does unstructured data actually live, and what is genuinely global versus per-claim? A model to react to, not a decision. The question the frame is meant to force: what do we deliberately not store, to avoid a data swamp?',
          render: () => <B3DataMap />,
        },
      ],
    },

    {
      id: 'C',
      title: 'Attendance Allowance, end to end',
      intent:
        'AA feels like the most likely first candidate to try end to end, so this is where the exploration goes deepest. Conversation-first, because that is the version most worth seeing working.',
      mode: 'sequence',
      fidelity: 'deep',
      frames: [
        {
          id: 'C1',
          name: 'The AA conversation',
          bare: true,
          inTheFrame:
            'The conversation, with the real AA form beside it so a casual answer can be watched becoming a graded field. The focus is the type of conversation and the language.',
          copy: {
            exchange: [
              { from: 'user', text: 'She can’t really wash herself.' },
              {
                from: 'us',
                text: 'Got it. When she washes or bathes, what does she need — a hand getting in and out, or someone nearby in case she slips?',
              },
              { from: 'user', text: 'Someone there really, she’s unsteady.' },
              { from: 'us', text: 'And is that most days, or now and then?' },
              {
                from: 'us',
                text: 'So: needs someone with her when she washes, most days, mainly because she’s unsteady. Have I got that right?',
              },
            ],
          },
          chasing:
            'The bit most worth getting right. Can a casual sentence become the detail the form actually needs — by probing like a CAM would — without inventing anything? The AA form beside it keeps us honest about what the form really asks for. The tension to feel: digging deep enough to earn the field, without it turning into an interrogation.',
          render: () => <C1Translation />,
        },
        {
          id: 'C2',
          name: 'When it needs a photo or a precise input',
          tap: 'Conversation hits something it can’t talk its way to',
          inTheFrame:
            'A mid-conversation moment where the answer needs a document or exact data (GP details, NI number, dates), with options: do it now, snap a photo, or come back to it.',
          copy: {
            lines: [
              'This bit needs their GP’s details. Want to snap a photo of a letter, type it in, or come back to it?',
            ],
          },
          chasing:
            'The open one that keeps coming up — where do the “later” items go? One thing worth trying is a “to finish” tray on the claim that the review stage and drop-in/out both read from. The question the sketch should raise: how many laters can stack before the claim feels stalled?',
          render: () => <C2PhotoOrPrecise />,
        },
        {
          id: 'C3',
          name: 'Confirm / edit, and where an answer came from',
          tap: 'A section completes',
          inTheFrame:
            'Inferred answers shown back in a scannable list, default yes, editing as the exception — each answer showing where it came from.',
          copy: {
            lines: [
              'Here’s what I’ve understood so far — tap anything that’s not right.',
              'Source tags: you told me · from a document · worked out from what you said',
            ],
          },
          chasing:
            'The trust piece — and the same pattern already circling in Magic Capture (confirm/edit plus “this is wrong”). How much do we surface at once, per section or all at review? And what must be confirmed no matter what?',
          render: () => <C3ConfirmEdit />,
        },
        {
          id: 'C4',
          name: 'Review stage',
          tap: 'All sections covered',
          inTheFrame:
            'The pre-submission view — everything captured, what is confirmed, what is still open (reading from the C2 “later” tray), and a path to submit.',
          copy: {
            lines: [
              'Almost there. This is everything we’ll send. 3 things still need you.',
            ],
          },
          chasing:
            'How much re-reading do we ask for, versus trusting the confirms already done section by section? And where is the route to a CAM if they are unsure?',
          render: () => <C4Review />,
        },
        {
          id: 'C5',
          name: 'The “never into the void” signal',
          tap: 'Runs alongside — after any answer lands',
          inTheFrame:
            'The little signal after an answer lands — a quiet “you’re closer” or “this strengthens the claim”.',
          copy: {
            lines: [
              'That helps — this is exactly the kind of detail AA turns on.',
            ],
          },
          chasing:
            'If the calculator is re-scoring in real time, this is what could be shown back. How often before it is noise? The aim is encouragement, not a slot machine.',
          render: () => <C5NotIntoTheVoid />,
        },
        {
          id: 'C6',
          name: 'When it stops asking',
          tap: 'Enough evidence gathered',
          inTheFrame:
            'The moment the conversation stops, because they clearly qualify (or clearly do not), and ends cleanly.',
          copy: {
            lines: [
              'You’ve told me enough — Margaret looks eligible. No need to go further on this bit.',
            ],
          },
          chasing:
            'Goal-seeking questioning, made visible. The thing to feel in the sketch: does it land as a win (“you’re through”), or like the app gave up? That is where the “no irrelevant questions” promise is kept or broken.',
          render: () => <C6WhenItStops />,
        },
      ],
    },

    {
      id: 'D',
      title: 'Ambient diary',
      intent:
        'Rough on purpose. The questions matter more than the frames here — each one is drawn to raise a fabrication-risk question rather than to answer it.',
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'D1',
          name: 'Where it lives, and how far it reaches',
          inTheFrame:
            'The diary entry point — probably from home — where the user just starts talking. The fork being sketched: does that start filling a claim, or land as a general note first?',
          copy: {
            exchange: [
              { from: 'user', text: 'Mum had two falls this week.' },
              {
                from: 'us',
                text: 'Sorry to hear that. I’ve noted it — this matters for her Attendance Allowance. Want to add any detail?',
              },
            ],
          },
          chasing:
            'How smart is it allowed to be? If someone just starts talking from the home page, can it start filling in a claim — does it have access to everything? Does one sentence route itself into the right fields (a fall touches mobility, supervision and night needs)? The more it reaches unprompted, the more the fabrication risk bites — that is the tension the frame should make visible.',
          render: () => <D1DiaryReach />,
        },
        {
          id: 'D2',
          name: 'Where diary notes live, and reviewing them',
          inTheFrame:
            'The diary as a reviewable, editable log — Granola-style: raw capture on one side, what we made of it on the other, editable.',
          copy: {
            caption:
              'Sketching diary notes as the unstructured layer from B3 — reviewable, correctable, only promoted into a claim answer on confirm.',
          },
          chasing:
            'In the absence of anywhere obvious, where do these notes live? There needs to be a way to review and edit them. And how do we show “what we made of your note” without it feeling like surveillance?',
          render: () => <D2DiaryReview />,
        },
        {
          id: 'D3',
          name: 'Calendar',
          maybe: true,
          inTheFrame:
            'A rough sketch of calendar and appointments feeding the diary — surgeries, meds, appointments captured passively.',
          copy: {
            lines: [
              'Add Margaret’s appointments and we’ll keep the time-based questions answered for you.',
            ],
          },
          chasing:
            'Could whole form sections be derived rather than asked? Do we even need calendar access for the diary to stay current? And does the value survive if people do not keep it up?',
          render: () => <D3Calendar />,
        },
      ],
    },

    {
      id: 'E',
      title: 'Dropping in and out',
      intent:
        'Coming back must never be a black box, and must never end in “now go enter it manually”. Two ways to honour that: meet them with a recap, or let them land on home and carry on if they feel like it.',
      mode: 'rivals',
      fidelity: 'sketch',
      frames: [
        {
          id: 'E1',
          name: 'A recap when you return',
          inTheFrame:
            'Someone closes the app mid-capture and comes back to a recap — what is captured, what is confirmed, what is left. Not a black box, not “start again”.',
          optimises:
            'Proof. It shows what it remembered before asking for anything, so trust is earned up front rather than assumed.',
          costs:
            'A gate. Every return trip starts with a screen to read and dismiss, including the trips that had nothing to do with the claim.',
          copy: {
            lines: [
              'Welcome back. Last time we covered how Margaret manages washing and getting around. Next up: help at night. Carry on?',
            ],
          },
          chasing:
            'How does session two know what session one held? It would need some kind of trigger or context it hangs onto. How much do we recap before it is friction — and how do we make sure the answer is never “now go enter it manually”?',
          render: () => <E1PickUp />,
        },
        {
          id: 'E2',
          name: 'Carry on from home',
          inTheFrame:
            'No return screen at all. You land on home, the unfinished claim is a task sitting above the others, and the orb is docked so talking is always available rather than being the thing you were interrupted doing.',
          optimises:
            'No ceremony. Coming back costs nothing, and the app never assumes the claim is why you opened it.',
          costs:
            'Proof. It cannot show what it remembered until you tap in, so the first moment asks for trust instead of earning it.',
          copy: {
            lines: [
              'Pick up where you left off — Help at night. About 3 questions to go.',
            ],
          },
          chasing:
            'If the orb is always there, does a resume task even need to exist, or would you just say “carry on”? And does an unfinished claim sitting on home read as helpful or as nagging — the same tension as the C2 “later” tray, one screen up.',
          render: () => <E2HomeResume />,
        },
      ],
    },
    {
      id: 'G',
      title: 'The form, and what fills it',
      intent:
        'One moment in the conversation, and everywhere it lands on the real Attendance Allowance form. Read conversation-first because that is the honest direction: a form is linear because paper is linear, and a conversation is not.',
      mode: 'probes',
      fidelity: 'rough',
      frames: [
        {
          id: 'G1',
          name: 'Form page, and the words that fill it',
          bare: true,
          interactive: true,
          wide: true,
          inTheFrame:
            'A real phone running the real conversation components on the left. On the right, every page of the genuine PDF that one moment lands on, with the fields it fills on each. Paged through by moment, not by form page.',
          chasing:
            'The scatter is the argument. One sentence about her nights fills three pages that a person would reach minutes apart, and one page is usually built from several moments — so the tangle is the thing conversation is better at, not a mess to tidy up. The open question underneath: does anything have to be asked in form order, and if so what?',
          render: () => <AaFormCarousel />,
        },
        {
          id: 'G2',
          name: 'What the model would be told',
          bare: true,
          inTheFrame:
            'The instruction the capture model would run under, written out — how to ask, what to do with an answer, when to stop, what must be exact, and what has to carry its provenance.',
          chasing:
            '“It asks like a good CAM would” is the load-bearing claim of this whole exploration, and a claim that vague cannot be argued with. Written out it becomes something engineering can price and the care team can correct. Worth noticing while reading it: almost none of these rules are about the model — they come from the form schema and from how a CAM already works, which is the argument for this being buildable rather than hopeful.',
          render: () => <AaPrompt />,
        },
      ],
    },
  ],

  walkthrough: [
    'Walk the high level flow first, so everyone has the whole story before anyone argues about a piece of it.',
    'Then the way in — A3 versus A4, and the payoff at A6 — since that is the fork that shapes everything else.',
    'Then go deep on C1, the AA translation moment, as the thing the whole idea rests on.',
    'B1 to B3 to show the app the calculator would produce.',
    'D and E as “here’s where this could go” sketches, rather than anything to decide — though E1 versus E2 is a real fork if the conversation model wins.',
    'A5 (care circle) and D3 (calendar) are the explicit maybes — in the room to be seen, not committed to.',
  ],

  thread:
    'The calculator as a possible spine, capture as the experience that could sit on top of it, and confirm/edit plus provenance as the same primitives already being explored in Magic Capture — two things that look separate but might be one interaction problem.',
};
